import React from "react";
import { Tag, Progress, Space, Tooltip, Alert } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  ThunderboltOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";

interface ProcessingStatusBadgeProps {
  status: string | null;
  progress: number | null;
  stage: string | null;
  // Raison de l'echec. Sans elle, un badge rouge n'apprend rien : il fallait
  // aller lire les journaux de media-processing-service pour savoir pourquoi.
  error?: string | null;
  type?: "video" | "trailer";
  compact?: boolean; // For use in table columns
}

const stageLabels: Record<string, string> = {
  DOWNLOADING: "Telechargement",
  TRANSCODING: "Transcodage",
  UPLOADING: "Upload HLS",
};

const stageIcons: Record<string, React.ReactNode> = {
  DOWNLOADING: <CloudDownloadOutlined />,
  TRANSCODING: <ThunderboltOutlined />,
  UPLOADING: <CloudUploadOutlined />,
};

export const ProcessingStatusBadge: React.FC<ProcessingStatusBadgeProps> = ({
  status,
  progress,
  stage,
  error = null,
  type = "video",
  compact = false,
}) => {
  if (!status) {
    return <Tag color="default">N/A</Tag>;
  }

  // Compact mode for table columns
  if (compact) {
    switch (status) {
      case "COMPLETED":
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            OK
          </Tag>
        );
      case "FAILED":
        return (
          <Tooltip title={error || "Cause non enregistree"}>
            <Tag color="error" icon={<CloseCircleOutlined />}>
              Erreur
            </Tag>
          </Tooltip>
        );
      case "PROCESSING":
        return (
          <Tag color="processing" icon={<SyncOutlined spin />}>
            {progress != null ? `${progress}%` : "..."}
          </Tag>
        );
      case "PENDING":
        return (
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            En attente
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  }

  // Full mode with progress bar
  const label = type === "trailer" ? "Bande-annonce" : "Video";

  switch (status) {
    case "COMPLETED":
      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {label} - Transcodage termine
          </Tag>
          <Progress percent={100} size="small" status="success" />
        </Space>
      );
    case "FAILED":
      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Tag color="error" icon={<CloseCircleOutlined />}>
            {label} - Transcodage echoue
          </Tag>
          <Progress percent={progress || 0} size="small" status="exception" />
          {error && (
            <Alert type="error" showIcon message="Cause" description={error} />
          )}
        </Space>
      );
    case "PROCESSING":
      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space size={8}>
            <Tag color="processing" icon={<SyncOutlined spin />}>
              {label} - En cours
            </Tag>
            {stage && (
              <Tag icon={stageIcons[stage]}>
                {stageLabels[stage] || stage}
              </Tag>
            )}
          </Space>
          <Progress
            percent={progress || 0}
            size="small"
            status="active"
            strokeColor={{
              "0%": "#108ee9",
              "100%": "#87d068",
            }}
          />
        </Space>
      );
    case "PENDING":
      return (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            {label} - En attente de transcodage
          </Tag>
          <Progress percent={0} size="small" />
        </Space>
      );
    default:
      return <Tag>{status}</Tag>;
  }
};

export default ProcessingStatusBadge;
